import { createI18n } from 'vue-i18n';
import { setUiLocale } from '@shiocast/ui';
import nl from './nl.js';
import en from './en.js';

/**
 * Panel localisation.
 *
 * Dutch is the default and the source language, because the station is Dutch and so is
 * everyone who runs it. English is the fallback rather than the other way round, which
 * means a missing Dutch string shows English rather than a raw key: an operator seeing
 * `schedule.readiness.blocked` during an incident is worse than one seeing it in the
 * wrong language.
 *
 * Broadcast vocabulary is deliberately not translated where the Dutch industry uses the
 * English term. "Playlist", "podcast", "stream" and "live" are what a Dutch radio maker
 * actually says; translating them to "afspeellijst" or "rechtstreeks" would read as
 * machine translation and be slower to scan, not faster.
 */
export const LOCALES = ['nl', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_NAMES: Readonly<Record<Locale, string>> = {
  nl: 'Nederlands',
  en: 'English',
};

/** The flag shown in the switcher, as V1 had it. */
export const LOCALE_FLAGS: Readonly<Record<Locale, string>> = {
  nl: '🇳🇱',
  en: '🇬🇧',
};

const STORAGE_KEY = 'shiocast.locale';

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/**
 * The locale to start in.
 *
 * A stored choice wins, then the browser's preference if we speak it, then Dutch. The
 * browser is consulted rather than assumed so a guest presenter on an English laptop is
 * not fighting the interface, but Dutch is the default for everyone else.
 */
export function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isLocale(stored)) return stored;
  } catch {
    // A browser with storage disabled still gets a working panel.
  }

  for (const preference of navigator.languages ?? []) {
    const base = preference.split('-')[0];
    if (base && isLocale(base)) return base;
  }

  return 'nl';
}

export const i18n = createI18n({
  legacy: false,
  locale: initialLocale(),
  fallbackLocale: 'en',
  messages: { nl, en },
  // A missing key is a bug we want to see in development and never want to show an
  // operator as a raw key, which the fallback locale handles.
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
});

export function setLocale(locale: Locale): void {
  i18n.global.locale.value = locale;
  document.documentElement.setAttribute('lang', locale);
  // Shared components format their own dates and weekday names; this keeps them in step
  // without making the component library depend on the panel's i18n.
  setUiLocale(localeTag(locale));

  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // The choice simply does not persist. Not worth failing over.
  }
}

/**
 * Format a date in the current locale and the station's timezone.
 *
 * Both matter and for different reasons: the locale decides the words and the order, the
 * timezone decides which day it is. A Dutch operator abroad wants Dutch weekday names and
 * Amsterdam times.
 */
export function localeTag(locale: Locale): string {
  return locale === 'nl' ? 'nl-NL' : 'en-GB';
}
